import React, { useState, useEffect, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  SelectChangeEvent,
} from "@mui/material";
import {
  CreateOrUpdateInvestmentParams,
  Investment,
  InvestmentTypeEnum,
} from "@bn/api/apiv1";
import { Currency } from "@api";
import { LoadingButton } from "@components";
import { isIsinError, useIsinValidation } from "@hooks";
import {
  EditTabContainer,
  EditTabFields,
  EditTabActions,
} from "./EditTabLayout";

interface Props {
  investment: Investment;
  currencies: Currency[];
  isSaving: boolean;
  error: AxiosApiError | null;
  initialComment: string;
  arePrivateNotesLoading: boolean;
  onSave: (data: CreateOrUpdateInvestmentParams, comment: string) => void;
}

const investmentTypes: InvestmentTypeEnum[] = [
  "11_stock",
  "21_fund",
  "22_etf",
  "31_bond",
  "51_certos",
  "61_pmetals",
  "71_massets",
  "81_fcurr",
  "91_managed",
];

export const InvestmentEditAdvancedTabContainer = ({
  investment,
  currencies = [],
  isSaving,
  error,
  initialComment,
  arePrivateNotesLoading,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const { isIsinValid } = useIsinValidation(investment);
  const hasIsinError = isIsinError(error);
  const [type, setType] = useState<InvestmentTypeEnum>(investment.type);
  const [isin, setIsin] = useState(isIsinValid ? (investment.isin ?? "") : "");
  const [wkn, setWkn] = useState(investment.wkn);
  const [currency, setCurrency] = useState(investment.currency);
  const [comment, setComment] = useState(initialComment);

  useEffect(() => {
    setComment(initialComment);
  }, [initialComment]);

  const handleValues = (
    event: ChangeEvent<{ value?: string }> | SelectChangeEvent<string>,
    key: "type" | "isin" | "wkn" | "currency"
  ) => {
    const value = event.target.value;

    if (key === "type") setType(value as InvestmentTypeEnum);
    if (key === "isin") setIsin(value as string);
    if (key === "wkn") setWkn(value as string);
    if (key === "currency") setCurrency(value as string);
  };

  const handleSave = () => {
    const data = {
      type,
      isin,
      wkn,
      currency,
    };

    onSave(data, comment);
  };

  return (
    <EditTabContainer>
      <EditTabFields>
        <FormControl>
          <InputLabel>
            {t("investmentEditPage.advancedTab.investmentType")}
          </InputLabel>
          <Select
            displayEmpty
            fullWidth
            value={type}
            onChange={(event) => handleValues(event, "type")}
          >
            {investmentTypes.map((item) => (
              <MenuItem key={item} value={item}>
                {t(`investmentTypes.${item}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          value={isin}
          onChange={(event) => handleValues(event, "isin")}
          label={t("investmentEditPage.advancedTab.isin")}
          error={hasIsinError}
          helperText={
            hasIsinError ? t("investmentsPage.isinInvalid") : undefined
          }
        />

        <TextField
          value={wkn}
          onChange={(event) => handleValues(event, "wkn")}
          label={t("investmentEditPage.advancedTab.wkn")}
        />

        {Boolean(currencies.length) && (
          <FormControl>
            <InputLabel>
              {t("investmentEditPage.advancedTab.investmentCurrency")}
            </InputLabel>
            <Select
              displayEmpty
              fullWidth
              value={currency}
              onChange={(event) => handleValues(event, "currency")}
            >
              {currencies.map(({ code, name }) => (
                <MenuItem key={code} value={code}>
                  {code} - {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          label={t("investmentEditPage.advancedTab.comment")}
          multiline
          rows={5}
          fullWidth
          disabled={arePrivateNotesLoading}
          sx={{ gridColumn: "1 / -1" }}
        />
      </EditTabFields>
      <EditTabActions>
        <LoadingButton
          isFetching={isSaving}
          variant="primary"
          disabled={isSaving}
          onClick={handleSave}
        >
          {t("investmentEditPage.advancedTab.saveButton")}
        </LoadingButton>
      </EditTabActions>
    </EditTabContainer>
  );
};
